import { useMemo, useState } from 'react';
import { PersonButton } from '@/features/people/PersonDrawer';
import { pickableLocations } from '@/lib/hooks';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { Bell, CalendarDays, Hash, ListChecks, MapPin, Pencil, Repeat, Trash2, Users, X } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CALENDAR_LAYER_COLORS, describeRecurrence, describeReminder, formatEventWhen,
  type CalendarEventView, type CalendarRecurrence, type CalendarScope, type EditMode, type Rsvp,
  rsvpGroups, rsvpHiddenText, rsvpPersonLine, rsvpSummary,
} from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { useAuth } from '@/lib/auth';
import { useConversations, useLocations, useMembers, useTeams, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { messageStore } from '@/lib/messageStore';
import { DialogFooter, Avatar, Badge, Button, Field, Input, Label, Modal, Select, Spinner, Textarea, Toggle } from '@/components/ui';
import { AudiencePicker, type AudienceValue } from '@/features/people/AudiencePicker';
import { EntityThread, useEntityThread } from '@/features/threads/EntityThread';
import { ItemReactions } from '@/components/reactions/ItemReactions';
import { expandInvitees, keepEndAfter, parseReminder, REMINDER_CHOICES, repeatToRule, ruleToRepeat, type RepeatChoice, type When } from './calendarUtils';

export const calKeys = {
  items: (ws: string) => ['calendar', ws] as const,
  event: (id: string) => ['calendar-event', id] as const,
  settings: (ws: string) => ['calendar-settings', ws] as const,
  perms: (ws: string) => ['calendar-perms', ws] as const,
};
export const invalidateCalendar = (ws: string, id?: string) => {
  void queryClient.invalidateQueries({ queryKey: calKeys.items(ws) });
  if (id) void queryClient.invalidateQueries({ queryKey: calKeys.event(id) });
};

export function useCalendarPerms() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: calKeys.perms(ws ?? ''), queryFn: () => api.calendarPermissions(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 });
}
export function useCalendarSettings() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: calKeys.settings(ws ?? ''), queryFn: () => api.calendarSettings(ws!), enabled: Boolean(ws) });
}

/** Start/end of one occurrence in local wall-clock time, honouring a one-off change. */
export function occurrenceTimes(e: CalendarEventView, occ?: string | null) {
  if (!occ || !e.recurrence) return { start: e.starts_local, end: e.ends_local };
  const o = e.overrides.find(x => x.occurrence === occ);
  const len = DateTime.fromISO(e.ends_local, { zone: 'utc' }).diff(DateTime.fromISO(e.starts_local, { zone: 'utc' })).as('minutes');
  const s = o?.starts_local ?? occ;
  return { start: s, end: o?.ends_local ?? DateTime.fromISO(s, { zone: 'utc' }).plus({ minutes: len }).toFormat("yyyy-MM-dd'T'HH:mm") };
}

const SCOPE_LABEL: Record<CalendarScope, string> = { personal: 'Personal', company: 'Company', team: 'Department', building: 'Building / site' };

/** Reminder chips with a quick-add menu and a custom value ("90m", "3h", "2d"). */
export function ReminderEditor({ value, onChange, allDay, defaults }: { value: number[] | null; onChange: (v: number[] | null) => void; allDay: boolean; defaults?: number[] }) {
  const [custom, setCustom] = useState('');
  const list = value ?? defaults ?? [];
  const add = (m: number) => onChange([...new Set([...list, m])].sort((a, b) => a - b).slice(0, 5));
  const choices = allDay ? [{ minutes: 0, label: 'On the day at 00:00' }, { minutes: 900, label: 'Day before at 09:00' }, { minutes: 1440, label: '1 day before (midnight)' }, { minutes: 2340, label: '2 days before at 09:00' }] : REMINDER_CHOICES;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {list.length === 0 && <span className="text-sm text-slate-500">No reminders</span>}
        {list.map(m => (
          <span key={m} className={clsx('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs', value === null ? 'bg-slate-100 text-slate-600 dark:bg-slate-800' : 'bg-accent-soft text-accent-fg')}>
            <Bell className="h-3 w-3" />{describeReminder(m, allDay)}
            <button type="button" aria-label={`Remove ${describeReminder(m, allDay)}`} onClick={() => onChange(list.filter(x => x !== m))}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {value === null && defaults && <span className="text-xs text-slate-500">(your defaults)</span>}
      </div>
      {list.length < 5 && (
        <div className="flex flex-wrap items-center gap-2">
          <Select aria-label="Add reminder" className="w-48" value="" onChange={e => e.target.value && add(Number(e.target.value))}>
            <option value="">Add a reminder…</option>
            {choices.filter(c => !list.includes(c.minutes)).map(c => <option key={c.minutes} value={c.minutes}>{c.label}</option>)}
          </Select>
          <Input aria-label="Custom reminder" className="w-28" placeholder="e.g. 90m, 3h" value={custom} onChange={e => setCustom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = parseReminder(custom); if (v !== null) { add(v); setCustom(''); } } }} />
          <Button type="button" size="sm" variant="ghost" disabled={parseReminder(custom) === null} onClick={() => { add(parseReminder(custom)!); setCustom(''); }}>Add</Button>
          {value !== null && defaults && <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)}>Use my defaults</Button>}
        </div>
      )}
    </div>
  );
}

export interface EditorInit { event?: CalendarEventView; occurrence?: string | null; date?: string; time?: string; allDay?: boolean; scope?: CalendarScope; conversationId?: string; taskId?: string }

/** Create / edit an event. Recurring events ask whether the change is for this event, this and following, or all. */
export function EventEditorModal({ init, onClose, onSaved }: { init: EditorInit; onClose: () => void; onSaved?: (id: string) => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const perms = useCalendarPerms().data;
  const settings = useCalendarSettings().data;
  const locations = useLocations().data?.locations ?? [];
  const convs = (useConversations().data?.conversations ?? []).filter(c => c.is_member && c.kind === 'channel');
  const e = init.event;
  const occ = init.occurrence ?? null;
  const times = e ? occurrenceTimes(e, occ) : null;
  const startDate = init.date ?? DateTime.now().setZone('Europe/London').toISODate()!;
  const t0 = init.time ?? '09:00';
  const [title, setTitle] = useState(e ? (e.overrides.find(o => o.occurrence === occ)?.title ?? e.title) : '');
  const [scope, setScope] = useState<CalendarScope>(e?.scope ?? init.scope ?? 'personal');
  const [teamId, setTeamId] = useState(e?.team_id ?? '');
  const [locationId, setLocationId] = useState(e?.location_id ?? '');
  const [allDay, setAllDay] = useState(e?.all_day ?? init.allDay ?? false);
  const [sDate, setSDate] = useState(times ? times.start.slice(0, 10) : startDate);
  const [sTime, setSTime] = useState(times && !e!.all_day ? times.start.slice(11) : t0);
  const [eDate, setEDate] = useState(times ? (e!.all_day ? DateTime.fromISO(times.end).minus({ days: 1 }).toISODate()! : times.end.slice(0, 10)) : startDate);
  const [eTime, setETime] = useState(times && !e!.all_day ? times.end.slice(11) : DateTime.fromFormat(t0, 'HH:mm').plus({ hours: 1 }).toFormat('HH:mm'));
  /** t353u: moving the start keeps the length; an end at or before the start snaps to start + 1 hour (all day: the same day). */
  const moveWhen = (patch: Partial<When>) => {
    const next = keepEndAfter({ sDate, sTime, eDate, eTime }, patch, allDay);
    setSDate(next.sDate); setSTime(next.sTime); setEDate(next.eDate); setETime(next.eTime);
  };
  const [repeat, setRepeat] = useState<RepeatChoice>(e ? ruleToRepeat(e.recurrence, e.starts_local) : 'none');
  const [endsKind, setEndsKind] = useState<'never' | 'until' | 'count'>(e?.recurrence?.until ? 'until' : e?.recurrence?.count ? 'count' : 'never');
  const [until, setUntil] = useState(e?.recurrence?.until ?? '');
  const [count, setCount] = useState(e?.recurrence?.count ?? 10);
  const [locationText, setLocationText] = useState(e?.location_text ?? '');
  const [description, setDescription] = useState(e?.description ?? '');
  // t681u: invite by name, or a whole department, group, position or building (expanded to the people in it now).
  const [who, setWho] = useState<AudienceValue>({ people: e?.attendees.map(a => a.user_id) ?? [], teams: [], buildings: [], positions: [] });
  const { byId: teamsById } = useTeams();
  const needStaff = Boolean(who.buildings?.length || who.positions?.length);
  const staffQ = useQuery({ queryKey: ['staff-directory', ws], queryFn: () => api.staffDirectory(ws!), enabled: Boolean(ws) && needStaff, staleTime: 60_000 });
  const attendees = useMemo(() => expandInvitees(who, staffQ.data?.staff ?? [], t => (teamsById.get(t)?.members ?? []).map(m => m.user_id)), [who, staffQ.data, teamsById]);
  const [conversationId, setConversationId] = useState(e?.conversation_id ?? init.conversationId ?? '');
  const [reminders, setReminders] = useState<number[] | null>(e ? e.my_reminders : null);
  const [mode, setMode] = useState<EditMode>(e?.recurrence && occ ? 'this' : 'all');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const scopes = useMemo(() => {
    const s: CalendarScope[] = ['personal'];
    if (perms?.company || e?.scope === 'company') s.push('company');
    if (perms?.teams.length || e?.scope === 'team') s.push('team');
    if (perms?.buildings.length || e?.scope === 'building') s.push('building');
    return s;
  }, [perms, e]);
  const onlyThis = Boolean(e?.recurrence) && mode === 'this';
  const defaults = allDay ? settings?.default_reminders_all_day : settings?.default_reminders_timed;

  const save = async () => {
    if (!ws) return;
    const start = allDay ? sDate : `${sDate}T${sTime}`;
    const end = allDay ? eDate : `${eDate}T${eTime}`;
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = 'Add a title';
    if (end < start || (!allDay && end === start)) errs.end = 'The end must be after the start';
    if (scope === 'team' && !teamId) errs.team = 'Choose a team';
    if (scope === 'building' && !locationId) errs.building = 'Choose a building / site';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const rule: CalendarRecurrence | null = repeatToRule(repeat, start.length === 10 ? `${start}T00:00` : start,
      { until: endsKind === 'until' ? until || null : null, count: endsKind === 'count' ? count : null }, e?.recurrence ?? undefined);
    setBusy(true);
    try {
      let saved: CalendarEventView;
      if (!e) {
        saved = await api.createEvent(ws, {
          scope, team_id: scope === 'team' ? teamId : null, location_id: locationId || null, title: title.trim(), description: description.trim() || null,
          location_text: locationText.trim() || null, all_day: allDay, start, end, recurrence: rule, attendee_ids: attendees, conversation_id: conversationId || null,
          task_id: init.taskId ?? null, reminders,
        });
      } else if (onlyThis) {
        const orig = times!;
        const origEnd = e.all_day ? DateTime.fromISO(orig.end).minus({ days: 1 }).toISODate()! : orig.end;
        const timesChanged = (allDay ? start !== orig.start.slice(0, 10) || end !== origEnd : start !== orig.start || end !== orig.end);
        saved = await api.updateEvent(e.id, {
          mode: 'this', occurrence: occ!, title: title.trim(), description: description.trim() || null, location_text: locationText.trim() || null,
          ...(timesChanged ? { start, end } : {}), reminders,
        });
      } else {
        const orig = times!;
        const origEnd = e.all_day ? DateTime.fromISO(orig.end).minus({ days: 1 }).toISODate()! : orig.end;
        const timesChanged = allDay !== e.all_day || (allDay ? start !== orig.start.slice(0, 10) || end !== origEnd : start !== orig.start || end !== orig.end);
        const ruleChanged = JSON.stringify(rule) !== JSON.stringify(e.recurrence);
        saved = await api.updateEvent(e.id, {
          mode, occurrence: e.recurrence ? occ ?? undefined : undefined,
          ...(scope !== e.scope || (teamId || null) !== e.team_id || (locationId || null) !== e.location_id ? { scope, team_id: scope === 'team' ? teamId : null, location_id: locationId || null } : {}),
          title: title.trim(), description: description.trim() || null, location_text: locationText.trim() || null,
          ...(timesChanged ? { all_day: allDay, start, end } : {}), ...(ruleChanged || mode === 'following' ? { recurrence: rule } : {}),
          attendee_ids: attendees, conversation_id: conversationId || null, reminders,
        });
      }
      invalidateCalendar(ws, e?.id);
      toast(e ? 'Event updated' : 'Event added', 'success');
      onSaved?.(saved.id);
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally { setBusy(false); }
  };

  return (
    <Modal open wide onClose={onClose} title={e ? 'Edit event' : 'New event'} footer={<DialogFooter onCancel={onClose} onSave={save} saving={busy} saveLabel={e ? 'Save' : 'Add event'} />}>
      <div className="grid gap-4 md:grid-cols-2">
        {e?.recurrence && occ && (
          <div className="md:col-span-2 rounded-lg bg-orange-50 p-3 text-sm dark:bg-orange-900/20" role="radiogroup" aria-label="Apply changes to">
            <div className="mb-1.5 font-medium">This is a repeating event. Change:</div>
            <div className="flex flex-wrap gap-4">
              {([['this', 'This event'], ['following', 'This and following events'], ['all', 'All events']] as const).map(([v, l]) => (
                <label key={v} className="flex items-center gap-1.5"><input type="radio" name="mode" checked={mode === v} onChange={() => setMode(v)} />{l}</label>
              ))}
            </div>
          </div>
        )}
        <Field label="Title" className="md:col-span-2">
          <Input aria-label="Title" autoFocus value={title} onChange={ev => setTitle(ev.target.value)} placeholder="e.g. Peak season briefing" maxLength={200} />
          {errors.title && <p className="mt-1 text-xs text-rose-600">{errors.title}</p>}
        </Field>
        {/* t682u: Description sits straight under Title. */}
        <Field label="Description" className="md:col-span-2"><Textarea aria-label="Description" rows={2} value={description} onChange={ev => setDescription(ev.target.value)} maxLength={4000} /></Field>
        {!onlyThis && (
          <Field label="Calendar">
            <Select aria-label="Calendar" value={scope} onChange={ev => setScope(ev.target.value as CalendarScope)}>
              {scopes.map(s => <option key={s} value={s}>{SCOPE_LABEL[s]}</option>)}
            </Select>
            <p className="mt-1 text-xs text-slate-500">{scope === 'personal' ? 'Private: only you and the people you invite can see it.' : scope === 'company' ? 'Everyone in the company sees it.' : scope === 'team' ? 'Members of the department see it.' : 'People at this building see it.'}</p>
          </Field>
        )}
        {!onlyThis && scope === 'team' && (
          <Field label="Department">
            <Select aria-label="Department" value={teamId} onChange={ev => setTeamId(ev.target.value)}>
              <option value="">Choose…</option>
              {(perms?.teams ?? []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
            {errors.team && <p className="mt-1 text-xs text-rose-600">{errors.team}</p>}
          </Field>
        )}
        {!onlyThis && (
          <Field label={scope === 'building' ? 'Building / site' : 'Building / site (optional)'}>
            <Select aria-label="Building" value={locationId} onChange={ev => setLocationId(ev.target.value)}>
              <option value="">{scope === 'building' ? 'Choose…' : 'None'}</option>
              {(scope === 'building' ? perms?.buildings ?? [] : pickableLocations(locations, locationId)).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
            {errors.building && <p className="mt-1 text-xs text-rose-600">{errors.building}</p>}
          </Field>
        )}
        <div className="md:col-span-2 space-y-2">
          {!onlyThis && <Toggle checked={allDay} onChange={setAllDay} label="All day" />}
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Starts"><div className="flex gap-1.5">
              <Input aria-label="Start date" type="date" value={sDate} onChange={ev => moveWhen({ sDate: ev.target.value })} />
              {!allDay && <Input aria-label="Start time" type="time" value={sTime} onChange={ev => moveWhen({ sTime: ev.target.value })} />}
            </div></Field>
            <Field label="Ends"><div className="flex gap-1.5">
              <Input aria-label="End date" type="date" value={eDate} onChange={ev => moveWhen({ eDate: ev.target.value })} />
              {!allDay && <Input aria-label="End time" type="time" value={eTime} onChange={ev => moveWhen({ eTime: ev.target.value })} />}
            </div></Field>
            <span className="pb-2 text-xs text-slate-500">UK time</span>
          </div>
          {errors.end && <p className="text-xs text-rose-600">{errors.end}</p>}
        </div>
        {!onlyThis && (
          <Field label="Repeat" className="md:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <Select aria-label="Repeat" className="w-56" value={repeat} onChange={ev => setRepeat(ev.target.value as RepeatChoice)}>
                <option value="none">Does not repeat</option>
                <option value="daily">Every day</option>
                <option value="weekdays">Every weekday (Mon-Fri)</option>
                <option value="weekly">Every week on {DateTime.fromISO(sDate).toFormat('cccc')}</option>
                <option value="fortnightly">Every 2 weeks on {DateTime.fromISO(sDate).toFormat('cccc')}</option>
                <option value="monthly">Every month on day {DateTime.fromISO(sDate).day}</option>
                <option value="yearly">Every year on {DateTime.fromISO(sDate).toFormat('d LLLL')}</option>
                {e?.recurrence && repeat === 'custom' && <option value="custom">{describeRecurrence(e.recurrence, e.starts_local)}</option>}
              </Select>
              {repeat !== 'none' && (<>
                <Select aria-label="Repeat ends" className="w-44" value={endsKind} onChange={ev => setEndsKind(ev.target.value as typeof endsKind)}>
                  <option value="never">Forever</option><option value="until">Until</option><option value="count">Number of times</option>
                </Select>
                {endsKind === 'until' && <Input aria-label="Repeat until" type="date" className="w-40" value={until} min={sDate} onChange={ev => setUntil(ev.target.value)} />}
                {endsKind === 'count' && <Input aria-label="Repeat count" type="number" className="w-20" min={1} max={1000} value={count} onChange={ev => setCount(Number(ev.target.value))} />}
              </>)}
            </div>
            {repeat !== 'none' && <p className="mt-1 text-xs text-slate-500">Repeats keep the same UK clock time, including across the clock changes.</p>}
          </Field>
        )}
        <Field label="Where"><Input aria-label="Where" value={locationText} onChange={ev => setLocationText(ev.target.value)} placeholder="e.g. Canteen, Teams link" maxLength={200} /></Field>
        {!onlyThis && (
          <Field label="Linked channel (optional)">
            <Select aria-label="Linked channel" value={conversationId} onChange={ev => setConversationId(ev.target.value)}>
              <option value="">None</option>
              {convs.map(c => <option key={c.id} value={c.id}>#{c.slug ?? c.title}</option>)}
            </Select>
          </Field>
        )}
        {!onlyThis && <Field label="Invite people" className="md:col-span-2"><AudiencePicker value={who} onChange={setWho} kinds={['person', 'department', 'group', 'position', 'building']}
          label="Invite people" placeholder="Search people, departments, groups, positions or buildings"
          helper={attendees.length ? <span data-testid="event-invite-reach">{attendees.length} {attendees.length === 1 ? 'person' : 'people'} invited{who.teams.length || who.buildings?.length || who.positions?.length ? ' (everyone in what you picked, as of now)' : ''}</span> : undefined} /></Field>}
        <div className="md:col-span-2"><Label>My reminders</Label><ReminderEditor value={reminders} onChange={setReminders} allDay={allDay} defaults={defaults} /></div>
      </div>
    </Modal>
  );
}

const RSVP_LABEL: Record<Rsvp, string> = { accepted: 'Going', tentative: 'Maybe', declined: 'Not going', pending: 'Not replied' };

export function EventDetailModal({ id, occurrence, onClose, onEdit }: { id: string; occurrence: string | null; onClose: () => void; onEdit: (e: CalendarEventView) => void }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: calKeys.event(id), queryFn: () => api.calendarEvent(id) });
  const settings = useCalendarSettings().data;
  const [askDelete, setAskDelete] = useState(false);
  const [delMode, setDelMode] = useState<EditMode>('this');
  const refresh = (v?: CalendarEventView) => { if (v) queryClient.setQueryData(calKeys.event(id), v); if (ws) invalidateCalendar(ws, id); };
  const thread = useEntityThread('calendar_event', id);
  // RSVPs and edits post lines into the discussion; fetch them straight away in case realtime is polling.
  const syncThread = () => { if (thread.data) void messageStore.catchUp(thread.data.conversation_id); void thread.refetch(); };
  const rsvp = useMutation({ mutationFn: (r: Exclude<Rsvp, 'pending'>) => api.rsvpEvent(id, r), onSuccess: v => { refresh(v); syncThread(); }, onError: err => toast(errorMessage(err), 'error') });
  const rem = useMutation({ mutationFn: (m: number[] | null) => api.updateEvent(id, { reminders: m }), onSuccess: v => { refresh(v); toast('Reminders saved', 'success'); }, onError: err => toast(errorMessage(err), 'error') });
  const del = useMutation({
    mutationFn: () => api.deleteEvent(id, q.data?.recurrence ? delMode : 'all', q.data?.recurrence ? occurrence ?? undefined : undefined),
    onSuccess: () => { if (ws) invalidateCalendar(ws); toast('Event deleted', 'success'); onClose(); }, onError: err => toast(errorMessage(err), 'error'),
  });
  const e = q.data;
  if (!e) return <Modal open onClose={onClose} title="Event">{q.isError ? <p className="text-sm text-rose-600">{errorMessage(q.error)}</p> : <Spinner />}</Modal>;
  const t = occurrenceTimes(e, occurrence);
  const o = e.overrides.find(x => x.occurrence === occurrence);
  const title = o?.title ?? e.title;
  const canRsvp = e.created_by !== userId && (e.scope !== 'personal' || e.my_rsvp !== null);
  const involved = e.created_by === userId || e.my_rsvp !== null;
  const scopeText = e.scope === 'company' ? 'Company event' : e.scope === 'team' ? `Team · ${e.team_name}` : e.scope === 'building' ? `Building · ${e.location_name}` : 'Personal · only you and invitees';
  return (
    <Modal open wide="xl" onClose={onClose} title={<span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: CALENDAR_LAYER_COLORS[e.scope] }} />{title}</span>}
      footer={e.can_edit ? (askDelete ? (
        <div className="flex w-full flex-wrap items-center gap-3">
          {e.recurrence ? (
            <div className="flex flex-wrap gap-3 text-sm" role="radiogroup" aria-label="Delete">
              {([['this', 'This event'], ['following', 'This and following'], ['all', 'All events']] as const).map(([v, l]) => (
                <label key={v} className="flex items-center gap-1.5"><input type="radio" name="del" checked={delMode === v} onChange={() => setDelMode(v)} />{l}</label>
              ))}
            </div>
          ) : <span className="text-sm">Delete this event?</span>}
          <span className="ml-auto flex gap-2"><Button variant="ghost" onClick={() => setAskDelete(false)}>Cancel</Button><Button variant="danger" loading={del.isPending} onClick={() => del.mutate()}>Delete</Button></span>
        </div>
      ) : (<>
        <Button variant="ghost" onClick={() => setAskDelete(true)}><Trash2 className="h-4 w-4" />Delete</Button>
        <Button variant="secondary" onClick={() => onEdit(e)}><Pencil className="h-4 w-4" />Edit</Button>
      </>)) : undefined}>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
      <div className="space-y-4 text-sm" data-testid="event-details">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-500" /><span className="font-medium">{formatEventWhen(t.start, t.end, e.all_day)}</span>
            {o && !o.cancelled && <Badge color="#ea580c">Changed for this date</Badge>}</div>
          {e.recurrence && <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300"><Repeat className="h-4 w-4 text-slate-500" />{describeRecurrence(e.recurrence, e.starts_local)}</div>}
          {(o?.location_text ?? e.location_text) && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-500" />{o?.location_text ?? e.location_text}</div>}
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300"><Users className="h-4 w-4 text-slate-500" />{scopeText} · organised by <PersonButton userId={e.created_by} name={e.created_by_name} className="rounded hover:underline">{e.created_by_name}</PersonButton></div>
          {e.conversation_name && <div className="flex items-center gap-2"><Hash className="h-4 w-4 text-slate-500" /><Link className="link" to={`/c/${e.conversation_id}`}>{e.conversation_name.replace(/^#/, '')}</Link></div>}
          {e.task_label && e.task_link && <div className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-slate-500" /><Link className="link" to={e.task_link}>{e.task_label}</Link></div>}
        </div>
        {(o?.description ?? e.description) && <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">{o?.description ?? e.description}</p>}
        {canRsvp && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">Going?</span>
            {(['accepted', 'tentative', 'declined'] as const).map(r => (
              <Button key={r} size="sm" variant={e.my_rsvp === r ? 'primary' : 'secondary'} aria-pressed={e.my_rsvp === r} loading={rsvp.isPending && rsvp.variables === r} onClick={() => rsvp.mutate(r)}>{RSVP_LABEL[r]}</Button>
            ))}
            {e.recurrence && <span className="text-xs text-slate-500">Applies to the whole series</span>}
          </div>
        )}
        {e.attendees.length > 0 && <EventAttendeeList event={e} />}
        <div>
          <div className="mb-1.5 font-medium">My reminders</div>
          {!involved && e.my_reminders === null && <p className="mb-1.5 text-xs text-slate-500">You're not invited, so you won't be reminded unless you add one.</p>}
          <ReminderEditor value={e.my_reminders ?? (involved ? null : [])} allDay={e.all_day} defaults={involved ? (e.all_day ? settings?.default_reminders_all_day : settings?.default_reminders_timed) : undefined}
            onChange={m => rem.mutate(m)} />
        </div>
      </div>
      {/* t260u: the event's own reactions, as on its feed card. */}
      <ItemReactions type="calendar_event" id={e.id} className="mb-2" />
      <EntityThread type="calendar_event" entityId={e.id} title="Discussion" className="flex h-[60vh] min-h-[22rem]"
        emptyText={e.scope === 'personal' ? 'No messages yet. Ask a question or share details with the people invited.' : 'No messages yet. Ask a question or share details with everyone going.'} />
      </div>
    </Modal>
  );
}

/**
 * t360u: who's coming, on desktop: "Invited (23) · 15 going · 2 maybe" then Going, Maybe, Can't go and Not replied,
 * each person with their avatar, name (opens the person drawer) and job · building muted. The same groups and rule as
 * the phone sheet (mobile/EventAttendees): Not replied names only for the organiser and managers who may edit it.
 */
export function EventAttendeeList({ event: e }: { event: Pick<CalendarEventView, 'attendees' | 'can_edit'> }) {
  const { byId } = useMembers();
  return (
    <div data-testid="event-attendees">
      <div className="mb-1.5 font-medium">Invited ({e.attendees.length}) <span className="font-normal text-slate-500" data-testid="event-rsvp-summary">· {rsvpSummary(e.attendees)}</span></div>
      <div className="space-y-2">
        {rsvpGroups(e.attendees, e.can_edit).map(g => (
          <section key={g.key} data-testid="event-attendee-group" data-rsvp={g.key}>
            <h3 className="mb-0.5 text-xs font-semibold text-slate-500">{g.hidden ? g.label : `${g.label} · ${g.people.length}`}</h3>
            {g.hidden ? <p className="text-xs text-slate-500" data-testid="event-attendees-hidden">{rsvpHiddenText(g.hidden)}</p> : (
              <ul className="grid gap-x-4 sm:grid-cols-2">
                {g.people.map(p => {
                  const line = rsvpPersonLine(p);
                  return (
                    <li key={p.user_id} className="flex min-w-0 items-center gap-2 py-1" data-testid="event-attendee">
                      <Avatar size={24} name={p.display_name} id={p.user_id} url={byId.get(p.user_id)?.avatar_url} />
                      <span className="min-w-0 flex-1 truncate">
                        <PersonButton userId={p.user_id} name={p.display_name} className="rounded hover:underline">{p.display_name}</PersonButton>
                        {line && <span className="text-xs text-slate-500"> · {line}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
